import { Fragment, type ReactNode } from "react";
import { Link } from "react-router-dom";

/**
 * Renders the small Markdown subset release notes actually use — `###`
 * headers, `- ` bullet lists, paragraphs, and `[text](url)` / `**bold**`
 * inline spans. Not a general Markdown renderer: release notes are
 * hand-written and reviewed in this repo, so the subset only needs to cover
 * what `releases/*.md` uses, not arbitrary input.
 */
export function ReleaseNotes({ body }: { body: string }) {
  const blocks = body.split(/\r?\n\r?\n+/).filter((b) => b.trim());

  return (
    <div className="release-notes">
      {blocks.map((block, i) => {
        const lines = block.split(/\r?\n/).map((l) => l.trim());

        const heading = /^###\s+(.+)$/.exec(lines[0] ?? "");
        if (heading) {
          // A heading isn't guaranteed to have a blank line after it before
          // more content in the same block — render whatever follows rather
          // than silently discarding it.
          const rest = lines.slice(1);
          return (
            <Fragment key={i}>
              <h4>{heading[1]}</h4>
              {rest.length > 0 && renderLines(rest, `${i}-rest`)}
            </Fragment>
          );
        }

        return renderLines(lines, i);
      })}
    </div>
  );
}

/** A block's lines, rendered as a bullet list (soft-wrapped continuation
 * lines folded into the item above them) or, failing that, a paragraph. */
function renderLines(lines: string[], key: number | string): ReactNode {
  if (lines[0]?.startsWith("- ")) {
    const items: string[] = [];
    for (const line of lines) {
      if (line.startsWith("- ")) {
        items.push(line.slice(2));
      } else if (items.length > 0) {
        items[items.length - 1] += ` ${line}`;
      }
    }
    return (
      <ul key={key}>
        {items.map((item, j) => (
          <li key={j}>{inline(item)}</li>
        ))}
      </ul>
    );
  }

  return <p key={key}>{inline(lines.join(" "))}</p>;
}

/** `[text](url)`, `**bold**`, and `` `code` `` — the only inline spans
 * release notes use. */
function inline(text: string): ReactNode {
  const parts = text.split(/(\[[^\]]+\]\([^)]+\)|\*\*[^*]+\*\*|`[^`]+`)/g);
  return (
    <>
      {parts.map((part, i) => {
        const link = /^\[([^\]]+)\]\(([^)]+)\)$/.exec(part);
        if (link) {
          const [, label, href] = link;
          return href!.startsWith("/") ? (
            <Link key={i} to={href!}>
              {label}
            </Link>
          ) : (
            <a key={i} href={href}>
              {label}
            </a>
          );
        }

        const bold = /^\*\*([^*]+)\*\*$/.exec(part);
        if (bold) {
          return <strong key={i}>{bold[1]}</strong>;
        }

        const code = /^`([^`]+)`$/.exec(part);
        if (code) {
          return (
            <code className="mono" key={i}>
              {code[1]}
            </code>
          );
        }

        return <Fragment key={i}>{part}</Fragment>;
      })}
    </>
  );
}
